import {DOCUMENT} from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker/datepicker';

import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import { CalendarPickerConfig } from '../../../../../utils/models/calendar.interface';
import moment from 'moment';
import { CalendarPeriodComponent } from './calendar-period/calendar-period.component';
import { CalendarMonthComponent } from './calendar-month/calendar-month.component';
import { CalendarComponent } from './calendar/calendar.component';
import { CalendarYearComponent } from './calendar-year/calendar-year.component';
import { Warning } from '../../../../../utils/models/input.interface';

const TIME_OUT = 600;
@Component({
  selector: 'calendar-picker',
  templateUrl: './calendar-picker.component.html',
  styleUrls: ['./calendar-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CalendarPickerComponent),
      multi: true,
    },
  ],
})
export class CalendarPickerComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input() calendarPicker: CalendarPickerConfig;
  @Input() showTime: boolean;
  @Input() id: string;
  @Input() textMonth = false;
  @Input() warning: Warning;
  @Input() useEndDate = false;
  @Input() viewFullMonth = false;
  @Input() notShowYear = false;

  private _disabled = false;
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(disabled: boolean) {
    this._disabled = disabled;
    this.setDisabledState(disabled);
  }

  @Output() dateChange: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('calendarPeriod') calendarPeriod: CalendarPeriodComponent;
  @ViewChild('calenarMonth') calenarMonth: CalendarMonthComponent;
  @ViewChild('calendar') calendar: CalendarComponent;
  @ViewChild('calendarYear') calendarYear: CalendarYearComponent;

  _yearPickerCtrl: FormControl = new FormControl();
  _monthPickerCtrl: FormControl = new FormControl();
  _dayPickerCtrl: FormControl = new FormControl();
  _dayPeriodPickerCtrl: FormControl = new FormControl();
  _regularPickerCtrl: FormControl = new FormControl();
  _periodPickerCtrl: FormControl = new FormControl();
  _yearPeriodPickerCtrl: FormControl = new FormControl();
  _monthPeriodPickerCtrl: FormControl = new FormControl();


  _startDate: any = [];
  _endDate: any = [];
  beforePeriod;
  resetStartDate: boolean;

  private readonly _onDestroy: Subject<void> = new Subject<void>();

  constructor(@Inject(DOCUMENT) private readonly _document: any) {}

  ngAfterViewInit() {
    switch (this.calendarPicker.mode) {
      case 'YEAR':
        this._subscribeToChanges(this._yearPickerCtrl);
        break;
      // case 'PERIOD':
      //   this._subscribeToChanges(this._periodPickerCtrl);
      //   break;

      case 'MONTH':
        this._subscribeToChanges(this._monthPickerCtrl);
        break;
      case 'YEAR PERIOD':
        this._subscribeToChanges(this._yearPeriodPickerCtrl);
        break;
      case 'MONTH PERIOD':
        this._subscribeToChanges(this._monthPeriodPickerCtrl);
        break;
      case 'DAY':
        this._subscribeToChanges(this._dayPickerCtrl)
      break;
      case 'DAY PERIOD':
        this._subscribeToChanges(this._dayPeriodPickerCtrl)
      break;
      default:
        this._subscribeToChanges(this._regularPickerCtrl);
    }
  }

  selectedEverything(event: any): void {
    this.beforePeriod = event;
  }

  ngOnDestroy() {
    this._onDestroy.next();
  }

  // Function to call when the date changes.
  onChange = (date: any) => {};

  // Function to call when the input is touched (when a star is clicked).
  onTouched = () => {};

  writeValue(date: any): void {
    this._writeValue(date);
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // Allows Angular to disable the input.
  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
    switch (this.calendarPicker.mode) {
      case 'YEAR':
        isDisabled ? this._yearPickerCtrl.disable() : this._yearPickerCtrl.enable();
        break;
      case 'MONTH':
        isDisabled ? this._monthPickerCtrl.disable() : this._monthPickerCtrl.enable();
        break;
      default:
        isDisabled ? this._regularPickerCtrl.disable() : this._regularPickerCtrl.enable();
    }
  }

  _takeFocusAway($datepicker: MatDatepicker<any>) {
    $datepicker.disabled = true;
    setTimeout(() => {
      $datepicker.datepickerInput['_elementRef'].nativeElement.blur();
      $datepicker.disabled = false;
    }, TIME_OUT);
  }

  private _writeValue(date: any): any {
    switch (this.calendarPicker.mode) {
      case 'YEAR':
          this._yearPickerCtrl.setValue(date, {emitEvent: false});
        break;
      case 'DAY PERIOD':
          this._dayPeriodPickerCtrl.setValue(date, { emitEvent: false });
        break;
      case 'YEAR PERIOD':
          this._yearPeriodPickerCtrl.setValue(date, { emitEvent: false });
        break;
      case 'MONTH PERIOD':
        this._monthPeriodPickerCtrl.setValue(date, { emitEvent: false });
      break;
      case 'MONTH':
          this._monthPickerCtrl.setValue(date, {emitEvent: false});
      break;
      case 'DAY':
          this._dayPickerCtrl.setValue(date, {emitEvent: false});
      break;

      default:
          this._regularPickerCtrl.setValue(date, {emitEvent: false});
    }
  }

  private _subscribeToChanges(control: FormControl) {
    if (!control) {
      return;
    }

    control.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe((value) => {
      let valor: any = [];
      if (value === undefined) {
        value = moment(this.beforePeriod.start_period);
        this.resetStartDate = true;
      }
      if (this.calendarPicker.mode === 'YEAR PERIOD' || this.calendarPicker.mode === 'MONTH PERIOD' || this.calendarPicker.mode === 'DAY PERIOD') {
        valor = this.emitterPeriod(value, this.calendarPicker.mode);
      } else {
        valor = new Date(value)
      }
      this.dateChange.emit(valor);
      this.onChange(valor);
      this.onTouched();

    });
  }

  private emitterPeriod(value, mode: string) {
    if(mode !== 'YEAR PERIOD' && mode !== 'MONTH PERIOD' && mode !== 'DAY PERIOD'){
      if (this._startDate instanceof Date && !this.resetStartDate) {
        this._endDate = new Date(value);
      } else {
        this._startDate = new Date(value);
        this.resetStartDate = false;
      }

      return {
        startDate: this._startDate,
        endDate: this._endDate
      }
    }else{
      return {
        startDate: new Date(value.start_period),
        endDate: value?.end_period ? new Date(value.end_period) : null
      }
    }
  }

  get _showMonthPicker(): boolean {
    return this.calendarPicker.mode === 'MONTH';
  }

  get _showRegularDatepicker(): boolean {
    return !this.calendarPicker.mode;
  }

  resetAll() {
    this.calendarPeriod?.reset()
    this.calenarMonth?.reset()
    this.calendar?.reset()
    this.calendarYear?.reset()
  }
}
